import { createSelector } from 'reselect';
import { ReduxState } from '../../../ReduxStorage/state';
import { selectWindowSize } from '../../../ReduxStorage/WindowSize/selectors';
import { CourseListingPageState, INITIAL_STATE } from './state';
import { CourseSearchFilters } from './types';
import { selectQueryObject } from '../../../ReduxStorage/location/selectors.common';

const selectState = (state: ReduxState): CourseListingPageState =>
  state.CourseListingPage || INITIAL_STATE;

export const selectInitialDataLoaded = createSelector(
  selectState,
  (state) => state.loaded
);

export const selectCategories = createSelector(
  selectState,
  (state) => state.categories || []
);

export const selectCategoryOptions = createSelector(
  selectCategories,
  (categories) =>
    categories.map((value) => ({
      label: value,
      value,
    }))
);

export const selectFeaturedInstitutions = createSelector(
  selectState,
  (state) => state.featuredInstitutions
);

export const selectFeaturedCourses = createSelector(
  selectState,
  (state) => state.featuredCourses
);

export const selectFilters = createSelector(selectQueryObject, (query) => {
  let filterValues: CourseSearchFilters = {};
  if (query.filters) {
    try {
      filterValues = JSON.parse(query.filters);
    } catch (e) {
      // ignore query param if JSON failed to parse
    }
  }
  return {
    categories: filterValues.categories,
    institutions: filterValues.institutions,
    feeTypes: filterValues.feeTypes,
    sortBy: filterValues.sortBy || ['popularity'],
    courseTypes: filterValues.courseTypes,
    courseSectors: filterValues.courseSectors,
    languages: filterValues.languages,
  };
});

export const selectCategoryValues = createSelector(
  selectFilters,
  (filters) => filters.categories || []
);

export const selectInstitutionValues = createSelector(
  selectFilters,
  (filters) => filters.institutions || []
);

export const selectFeeTypeValues = createSelector(
  selectFilters,
  (filters) => filters.feeTypes || []
);

export const selectSortByValues = createSelector(
  selectFilters,
  (filters) => filters.sortBy || []
);

export const selectCourseTypeValues = createSelector(
  selectFilters,
  (filters) => filters.courseTypes || []
);

export const selectCourseSectorValues = createSelector(
  selectFilters,
  (filters) => filters.courseSectors || []
);

export const selectCourseLanguageValues = createSelector(
  selectFilters,
  (filters) => filters.languages || []
);

export const selectSearchByCourseFilters = createSelector(
  selectFilters,
  (filters) => ({
    profileName: null,
    query: '',
    categories: filters.categories,
    institutionPaths: filters.institutions,
    feeTypes: filters.feeTypes,
    sortBy: filters.sortBy[0],
    classifiers: filters.courseTypes,
    courseSectors: filters.courseSectors,
    languages: filters.languages,
  })
);

export const selectHasFiltersSelected = createSelector(
  selectFilters,
  (filters) =>
    Boolean(
      filters.categories ||
        filters.institutions ||
        filters.feeTypes ||
        filters.courseTypes ||
        filters.courseSectors ||
        filters.languages
    )
);

const selectSearchResults = createSelector(
  selectState,
  (state) => state.searchResults
);

export const selectFilteredCourses = createSelector(
  selectSearchResults,
  (searchResults) => searchResults.courses
);

export const selectTotalResults = createSelector(
  selectSearchResults,
  (searchResults) => searchResults.total
);

const MOBILE_BREAKPOINT = 980;
export const selectIsMobileMode = createSelector(
  selectWindowSize,
  (windowSize) =>
    Boolean(windowSize.width && windowSize.width <= MOBILE_BREAKPOINT)
);

export const selectIsProcessing = createSelector(
  selectState,
  (state) => state.processing
);

export const selectError = createSelector(
  selectState,
  (state) => state.error || ''
);

export const selectCurrentRequestId = createSelector(
  selectState,
  (state) => state.requestId
);

export const selectShowMore = createSelector(
  selectIsProcessing,
  selectFilteredCourses,
  selectTotalResults,
  (processing, courses, total) => !processing && courses.length < total
);
